import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useEditor } from "../../../hooks/useEditor";
import createJustifiedLayout from "justified-layout";

import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useDefinition } from "../../../hooks/useDefinition";
import { useDefinitionFeatures } from "../../../hooks/useDefinitionFeatures";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useResetDefinitionForDesign } from "../../../hooks/useResetDefinitionForDesign";
import { useResizeObserver } from "../../../hooks/useResizeObserver";
import { MenuIds, useMenus } from "../../../hooks/useMenus";
import { useDesigns } from "../../bottom-designs-menus/use-designs";

import "./sidebar-designs-panel.css";
import { scrollIntoView } from "../../../utils/dom";
import { ISidebarContentRef, SidebarPanel } from "../sidebar-panel";
import { PlaceholderImage } from "../../placeholder";

const SidebarDesignsItem = ({
	thumbUrl,
	isActive,
	onClick,
	style,
}: {
	thumbUrl: string;
	isActive?: boolean;
	onClick?(): void;
	style?: React.CSSProperties;
}) => (
	<button
		className={"sidebar-designs-item"}
		aria-selected={isActive}
		{...{ "data-checked": isActive || undefined }}
		style={style}
		role="option"
		onClick={(e) => {
			e.stopPropagation();
			if (onClick) onClick();
		}}
	>
		<PlaceholderImage className="sidebar-designs-item__img" src={thumbUrl} loading="lazy" />
	</button>
);

export const SidebarDesignsPanel = memo(() => {
	const editor = useEditor();
	const { client } = editor;
	const { data: designs = [] } = useDesigns();
	const panelRef = useRef<ISidebarContentRef | null>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const isInitialScrollRef = useRef(true);
	const [containerWidth, setContainerWidth] = useState(0);

	const { areDesignsSupported } = useDefinitionFeatures();
	const {
		actions: { setMenu },
	} = useMenus();

	useEffect(() => {
		if (!areDesignsSupported) {
			setMenu(MenuIds.Root);
		}
	}, [areDesignsSupported, setMenu]);

	const dashboard = useDashboardContext();
	const { createErrorHandler } = useErrorHandling();

	const definition = useDefinition();
	const predefinedProductName = definition?.product?.predefinedName;

	const [definitionToReuse, setDefinitionToReuse] = useResetDefinitionForDesign();

	// Measure initial width for grid
	useLayoutEffect(() => {
		if (gridRef.current) {
			setContainerWidth(gridRef.current?.getBoundingClientRect().width ?? 0);
		}
	}, []);

	// Watch width changes for grid
	useResizeObserver(gridRef, () => {
		setContainerWidth(gridRef.current?.getBoundingClientRect().width ?? 0);
	});

	const showGrid = containerWidth > 0;

	const { boxes, containerHeight } = useMemo(() => {
		if (!designs?.length || !containerWidth) {
			return { boxes: null, containerHeight: 0 };
		}

		// We can later get the sidebarPanelPadding from  CSS variable
		const boxSpacing = 8;
		const sidebarPanelPadding = 12; // sidebarPanelPadding - borderThickness
		return createJustifiedLayout(
			designs.map(() => 1),
			{
				containerPadding: sidebarPanelPadding,
				containerWidth,
				targetRowHeight: 150,
				boxSpacing,
				forceAspectRatio: 1,
			},
		);
	}, [containerWidth, designs]);

	// Scroll to active element
	useEffect(() => {
		// Currently based on index, no attribute
		const index = designs.findIndex((d) => d.predefinedProductName === predefinedProductName);
		const el = gridRef.current?.children.item(index);
		if (el) {
			scrollIntoView(el, {
				/* smooth does not work by default as button scrolls automatically by browser when it is clicked and gets focused */
				behavior: "instant",
				container: panelRef.current?.scrollableEl,
				margins: eXpress.math.margins(0, 50), // add some margins so the active item is not at the very edge of the screen
			});
			isInitialScrollRef.current = false;
		}
		// boxes added in deps because, that draws the elements we search for
	}, [predefinedProductName, designs, boxes]);

	const { busyBlocker } = useBusyBlockerContext();

	const onClick = (newPredefinedProductName: eXpress.PredefinedProductName) => {
		if (!definition) return;

		if (!definitionToReuse) {
			const copyApiData = editor.client.copyApiData.bind(editor.client);
			setDefinitionToReuse(definition.clone(copyApiData));
		}

		busyBlocker
			.during(
				dashboard.applyAlternativeDesignAsync(newPredefinedProductName, definitionToReuse),
			)
			.catch(createErrorHandler());
	};

	return (
		<SidebarPanel.Root>
			<SidebarPanel.Content
				ref={panelRef}
				className="sidebar-designs-panel"
				scrollable
				fade
				noPadding
			>
				{/* use Sidebar.Content scrollable fade */}
				<div
					ref={gridRef}
					className="sidebar-designs-panel__grid"
					role="listbox"
					style={{ height: containerHeight }}
				>
					{showGrid &&
						boxes?.map((box, index) => {
							const design = designs[index];
							return (
								<div
									key={design.predefinedProductName}
									style={{
										position: "absolute",
										top: box?.top,
										left: box?.left,
										width: box?.width,
										height: box?.height,
									}}
								>
									<SidebarDesignsItem
										thumbUrl={design.packshotUrl.getAbsoluteUrl(client)}
										isActive={
											predefinedProductName === design.predefinedProductName
										}
										onClick={() => {
											onClick(design.predefinedProductName);
										}}
									/>
								</div>
							);
						})}
				</div>
			</SidebarPanel.Content>
		</SidebarPanel.Root>
	);
});
SidebarDesignsPanel.displayName = "SidebarDesignsPanel";
